/**
 * DealsKanbanPage — default Deals view. One column per stage of the
 * default (or selected) pipeline. Drag a deal card across columns to
 * move stage (uses @dnd-kit/core for the drag-drop).
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  defaultDropAnimationSideEffects,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DropAnimation,
} from '@dnd-kit/core';
import { LayoutGrid, List, Plus } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsPipelines,
  useFiasLeadsPipelineSummary,
  useMoveFiasLeadsDealStage,
  useCreateFiasLeadsDeal,
} from '../hooks/useFiasLeads';
import type { FiasLeadsDealDto } from '@/lib/api';
import { SkeletonBlock } from '../components/Skeletons';

interface Props {
  holderId: string;
}

// Drop animation eases the overlay from the cursor onto the moved
// card's new column. dnd-kit's default keyframes measure the active
// draggable at animation time, so they pick up the post-optimistic
// position automatically — provided the optimistic cache update has
// landed by then. That synchronization is enforced in
// useMoveFiasLeadsDealStage's onMutate (setQueryData runs before any
// await, so it batches into the same React commit that fires the
// animation). sideEffects keeps the underlying card invisible for the
// duration so we don't double-render overlay + card mid-glide.
const dropAnimation: DropAnimation = {
  duration: 220,
  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.1)',
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0' } },
  }),
};

export function DealsKanbanPage({ holderId }: Props) {
  const pipelinesQuery = useFiasLeadsPipelines(holderId);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);

  const pipelines = pipelinesQuery.data ?? [];
  const activePipelineId =
    selectedPipelineId ?? pipelines.find((p) => p.is_default)?.id ?? pipelines[0]?.id ?? null;

  if (pipelinesQuery.isLoading) return <SkeletonBlock lines={6} />;
  if (pipelines.length === 0) {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center">
        <h1 className="text-2xl font-semibold">Deals</h1>
        <p className="text-sm text-muted-foreground mt-2">
          No pipelines yet. Set one up before you can track deals.
        </p>
        <Link
          to={`${ARCHE_PATH}/settings/pipelines`}
          className="inline-block mt-4 rounded bg-primary px-4 py-2 text-primary-foreground text-sm"
        >
          Create your first pipeline
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Deals</h1>
          <p className="text-sm text-muted-foreground">
            Drag a card between columns to move stage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={activePipelineId ?? ''}
            onChange={(e) => setSelectedPipelineId(e.target.value)}
            className="rounded border border-border bg-card px-3 py-2 text-sm"
          >
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
          <Link
            to={`${ARCHE_PATH}/deals/list`}
            className="inline-flex items-center gap-2 rounded border border-border px-3 py-2 text-sm"
            title="List view"
          >
            <List className="h-4 w-4" />
            List
          </Link>
          <div className="inline-flex items-center gap-2 rounded bg-muted/40 px-3 py-2 text-sm">
            <LayoutGrid className="h-4 w-4" />
            Board
          </div>
        </div>
      </header>

      {activePipelineId && <KanbanBoard holderId={holderId} pipelineId={activePipelineId} />}
    </div>
  );
}

function KanbanBoard({ holderId, pipelineId }: { holderId: string; pipelineId: string }) {
  const summaryQuery = useFiasLeadsPipelineSummary(holderId, pipelineId);
  const moveDeal = useMoveFiasLeadsDealStage(holderId);
  const createDeal = useCreateFiasLeadsDeal(holderId);
  const [activeDeal, setActiveDeal] = useState<FiasLeadsDealDto | null>(null);
  const [newDealStageId, setNewDealStageId] = useState<string | null>(null);
  const [newDealName, setNewDealName] = useState('');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const stages = useMemo(() => summaryQuery.data ?? [], [summaryQuery.data]);

  if (summaryQuery.isLoading) return <SkeletonBlock lines={6} />;

  const handleDragStart = (e: DragStartEvent) => {
    const dealId = e.active.id as string;
    for (const stage of stages) {
      const deal = stage.deals.find((d) => d.id === dealId);
      if (deal) {
        setActiveDeal(deal);
        return;
      }
    }
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveDeal(null);
    if (!e.over) return;
    const dealId = e.active.id as string;
    const targetStageId = e.over.id as string;
    let sourceStageId: string | undefined;
    for (const stage of stages) {
      if (stage.deals.some((d) => d.id === dealId)) {
        sourceStageId = stage.stage_id;
        break;
      }
    }
    if (!sourceStageId || sourceStageId === targetStageId) return;
    await moveDeal.mutateAsync({ dealId, stageId: targetStageId });
  };

  const handleCreateDeal = async (stageId: string) => {
    if (!newDealName.trim()) return;
    await createDeal.mutateAsync({
      pipelineId,
      stageId,
      name: newDealName.trim(),
    });
    setNewDealName('');
    setNewDealStageId(null);
  };

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => (
          <StageColumn key={stage.stage_id} stage={stage}>
            {stage.deals.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
            {newDealStageId === stage.stage_id ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreateDeal(stage.stage_id);
                }}
                className="rounded border border-border bg-background p-2"
              >
                <input
                  type="text"
                  placeholder="Deal name"
                  value={newDealName}
                  onChange={(e) => setNewDealName(e.target.value)}
                  className="w-full rounded border border-border bg-card px-2 py-1 text-sm"
                  autoFocus
                />
                <div className="flex gap-1 mt-1">
                  <button
                    type="submit"
                    disabled={!newDealName.trim()}
                    className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNewDealStageId(null);
                      setNewDealName('');
                    }}
                    className="rounded border border-border px-2 py-1 text-xs"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setNewDealStageId(stage.stage_id)}
                className="flex items-center justify-center gap-1 rounded border border-dashed border-border py-2 text-xs text-muted-foreground hover:text-foreground"
              >
                <Plus className="h-3 w-3" />
                Add deal
              </button>
            )}
          </StageColumn>
        ))}
      </div>
      <DragOverlay dropAnimation={dropAnimation}>
        {activeDeal && (
          <div className="rounded border border-border bg-card p-3 shadow-lg w-64">
            <p className="text-sm font-medium">{activeDeal.name}</p>
            <p className="text-xs text-muted-foreground">
              {activeDeal.currency} {Number(activeDeal.value).toLocaleString()}
            </p>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function StageColumn({
  stage,
  children,
}: {
  stage: { stage_id: string; name: string; deals: FiasLeadsDealDto[] };
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.stage_id });
  const total = stage.deals.reduce((sum, d) => sum + Number(d.value), 0);
  return (
    <div
      ref={setNodeRef}
      className={
        'flex-shrink-0 w-72 rounded border border-border bg-muted/20 p-2 transition-colors ' +
        (isOver ? 'border-primary bg-primary/5' : '')
      }
    >
      <header className="flex items-center justify-between mb-2 px-1">
        <div>
          <h3 className="text-sm font-medium">{stage.name}</h3>
          <p className="text-xs text-muted-foreground">
            {stage.deals.length} · ${total.toLocaleString()}
          </p>
        </div>
      </header>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

function DealCard({ deal }: { deal: FiasLeadsDealDto }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: deal.id,
  });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0 : 1,
      }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="rounded border border-border bg-card p-3 cursor-grab active:cursor-grabbing"
    >
      <Link
        to={`${ARCHE_PATH}/deals/${deal.id}`}
        onClick={(e) => e.stopPropagation()}
        className="text-sm font-medium hover:underline"
      >
        {deal.name}
      </Link>
      <p className="text-xs text-muted-foreground mt-1">
        {deal.currency} {Number(deal.value).toLocaleString()}
      </p>
      {deal.expected_close_date && (
        <p className="text-xs text-muted-foreground">
          close: {new Date(deal.expected_close_date).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}
